import { MobileChatDrawerType } from '@/types/chat'
import { Message } from '@/types/message'
import { atom } from 'recoil'
import { ChattedCharacterListRes } from '@/types/character'
import {
  localStorageWriteJsonEffect,
  localStorageWriteStringEffect,
} from './util'
import { DEFAULT_CHAT_BUBBLE_COLOR } from '@/utils/chatBubble'

export const chatShareInfoState = atom<Map<string, boolean>>({
  key: 'chatShareInfoState',
  default: new Map(),
})

// 当前聊天被选择的消息
export const chatFeedbackMessage = atom<Message | undefined>({
  key: 'chatFeedbackMessage',
  default: undefined,
})

export const mobileChatDrawerTypeState = atom<MobileChatDrawerType>({
  key: 'mobileChatDrawerTypeState',
  default: 'profile',
})

export const mobileChatDrawerOpenState = atom<boolean>({
  key: 'mobileChatDrawerOpenState',
  default: false,
})

export const mobileChatLikeState = atom<boolean>({
  key: 'mobileChatLikeState',
  default: false,
})

export const mobileChatDrawerParamsState = atom<any>({
  key: 'mobileChatDrawerParamsState',
  default: null,
})

export const characterProfileState = atom<ChattedCharacterListRes>({
  key: 'characterProfileState',
  default: undefined,
})

// 进入编辑时记录正在编辑的消息, 同时也用作在全局判断是否是编辑状态
export const chatEditingMessageState = atom<Message | undefined>({
  key: 'chatEditingMessageState',
  default: undefined,
})

export const chatListState = atom<Message[]>({
  key: 'chatListState',
  default: [],
})

export type ChatInspirationSource = 'pre_generated' | 'on_demand'

export type ChatInspirationState = {
  characterId: string | null
  messageId: string | null
  inspirations: string[]
  source: ChatInspirationSource | null
}

export const emptyChatInspirationState: ChatInspirationState = {
  characterId: null,
  messageId: null,
  inspirations: [],
  source: null,
}

export const chatInspirationState = atom<ChatInspirationState>({
  key: 'chatInspirationState',
  default: emptyChatInspirationState,
})

export type InspirationActiveExposureState = {
  messageId: string | null
  used: boolean
}

export type InspirationExperimentUserState = {
  consecutiveUnusedCount: number
  autoClosed: boolean
  activeExposureByScope: Record<string, InspirationActiveExposureState>
  totalHintsShownBeforeClose: number
  totalHintsUsedBeforeClose: number
  totalRegeneratesBeforeClose: number
  totalAiRepliesBeforeClose: number
}

export type InspirationAutoClosePayload = {
  consecutive_unused_count: number
  total_hints_shown_before_close: number
  total_hints_used_before_close: number
  total_regenerates_before_close: number
  total_ai_replies_before_close: number
}

export type RecordAutoShowResult = {
  shouldShow: boolean
  autoClosePayload?: InspirationAutoClosePayload
}

export type InspirationExperimentState = Record<
  string,
  InspirationExperimentUserState
>

export const inspirationExperimentState = atom<InspirationExperimentState>({
  key: 'inspirationExperimentState',
  default: {},
  effects: [localStorageWriteJsonEffect('inspirationExperimentState')],
})

export const chatRetellMessageCache = atom<{
  cache: Message[]
  index: number
  serverIndex: number
}>({
  key: 'chatRetellMessageCache',
  default: { cache: [], index: -1, serverIndex: -1 },
})

export const gemNotEnoughModalOpenState = atom<boolean>({
  key: 'gemNotEnoughModalOpenState',
  default: false,
})

// Tidal Hearts 鸡尾酒库存/余额不足弹窗
export const noDrinksModalOpenState = atom<boolean>({
  key: 'noDrinksModalOpenState',
  default: false,
})

// No Drinks 弹窗展示的角色名（全局挂载的弹窗拿不到当前角色，需 store 中转）
export const noDrinksModalCharacterNameState = atom<string | undefined>({
  key: 'noDrinksModalCharacterNameState',
  default: undefined,
})

export const luckyEasterEggNotEnoughModalOpenState = atom<boolean>({
  key: 'luckyEasterEggNotEnoughModalOpenState',
  default: false,
})

export const goldenEasterEggNotEnoughModalOpenState = atom<boolean>({
  key: 'goldenEasterEggNotEnoughModalOpenState',
  default: false,
})

export const GemsAndSubscriptionPageSource = atom<string>({
  key: 'GemsAndSubscriptionPageSource',
  default: '',
})

export const newUserGemNotEnoughModalOpenState = atom<boolean>({
  key: 'newUserGemNotEnoughModalOpenState',
  default: false,
})

export const newUserGemNotEnough = atom<boolean>({
  key: 'newUserGemNotEnough',
  default: false,
})

export const fromNewUserGuide = atom<boolean>({
  key: 'fromNewUserGuide',
  default: false,
})

export const openChatMediaFocusMode = atom<boolean>({
  key: 'openChatMediaFocusMode',
  default: false,
  effects: [localStorageWriteStringEffect('openChatMediaFocusMode')],
})

// 大屏模式下的媒体焦点状态，控制是否只展示最新一条消息，不需要保存到 localStorage
export const bigScreenFocusMode = atom<boolean>({
  key: 'bigScreenFocusMode',
  default: true, // 默认开启
})

export const defaultImageListState = atom<string[]>({
  key: 'defaultImageListState',
  default: [],
  effects: [localStorageWriteJsonEffect('defaultImageListState')],
})

export const roleCardLimitModalOpenState = atom<boolean>({
  key: 'roleCardLimitModalOpenState',
  default: false,
})

export const roleCardRedDotState = atom<boolean>({
  key: 'roleCardRedDotState',
  default: true,
})

// 聊天设置红点提醒状态
/** @deprecated */
// export const chatSettingsRedDotState = atom<boolean>({
//   key: 'chatSettingsRedDotState',
//   default: true, // 默认显示红点，用户查看后设为 false
//   effects: [localStorageWriteJsonEffect('chatSettingsRedDot')],
// })

export const chatMediaAutoImgEnabledState = atom<boolean | null>({
  key: 'chatMediaAutoImgEnabledState',
  default: null,
})

export const chatMediaAutoImgLoadingState = atom<boolean>({
  key: 'chatMediaAutoImgLoadingState',
  default: false,
})

export const chatMediaAutoVideoEnabledState = atom<boolean | null>({
  key: 'chatMediaAutoVideoEnabledState',
  default: null,
})

export const chatMediaAutoVideoLoadingState = atom<boolean>({
  key: 'chatMediaAutoVideoLoadingState',
  default: false,
})

// 语音自动播放状态
export const chatMediaAutoAudioEnabledState = atom<boolean | null>({
  key: 'chatMediaAutoAudioEnabledState',
  default: null,
})

// 语音自动播放加载状态
export const chatMediaAutoAudioLoadingState = atom<boolean>({
  key: 'chatMediaAutoAudioLoadingState',
  default: false,
})

// 语音播放确认状态 - 用于跟踪用户是否已经确认过手动播放语音
export const voicePlayConfirmedState = atom<boolean>({
  key: 'voicePlayConfirmedState',
  default: false,
})

// 消息音频状态Map，用于跟踪每条消息的音频播放状态
export const messageAudioStatusMap = atom<
  Map<string, { isPlaying: boolean; isLoading: boolean }>
>({
  key: 'messageAudioStatusMap',
  default: new Map(),
})

export const AutoImgGenerateTooltipState = atom<boolean>({
  key: 'AutoImgGenerateTooltipState',
  default: false,
})

// 新手任务触发Live Studio弹窗的状态
export const NewbieTaskLiveStudioOpenState = atom<boolean>({
  key: 'NewbieTaskLiveStudioOpenState',
  default: false,
})

// 新手任务图片生成完成后Live Studio弹窗状态
export const NewbieTaskPostGenerationLiveStudioState = atom<boolean>({
  key: 'NewbieTaskPostGenerationLiveStudioState',
  default: false,
})
// Live Studio 抽屉打开状态
export const liveStudioDrawerOpenState = atom<boolean>({
  key: 'liveStudioDrawerOpenState',
  default: false,
})

// Pinned Memory弹窗打开状态
export const pinnedMemoryOpenState = atom<boolean>({
  key: 'pinnedMemoryOpenState',
  default: false,
})

// 视频背景音乐开启状态
export const chatVideoSoundEnabledState = atom<boolean | null>({
  key: 'chatVideoSoundEnabledState',
  default: true, // 默认开启
  effects: [localStorageWriteJsonEffect('videoSoundEnabled')],
})

// 视频背景音乐加载状态
export const chatVideoSoundLoadingState = atom<boolean>({
  key: 'chatVideoSoundLoadingState',
  default: false,
})

// 首次进入时显示视频声音引导
/** @deprecated */
// export const showVideoSoundGuideOnFirstEntryState = atom<boolean>({
//   key: 'showVideoSoundGuideOnFirstEntryState',
//   default: true,
//   effects: [localStorageWriteJsonEffect('showVideoSoundGuideOnFirstEntry')],
// })

// 自动生成视频时显示声音引导
/** @deprecated */
// export const showVideoSoundGuideOnAutoVideoState = atom<boolean>({
//   key: 'showVideoSoundGuideOnAutoVideoState',
//   default: false,
//   effects: [localStorageWriteJsonEffect('showVideoSoundGuideOnAutoVideo')],
// })

// PC端首次进入时显示视频声音引导
// export const showVideoSoundGuideOnFirstEntryDesktopState = atom<boolean>({
//   key: 'showVideoSoundGuideOnFirstEntryDesktopState',
//   default: true,
//   effects: [
//     localStorageWriteJsonEffect('showVideoSoundGuideOnFirstEntryDesktop'),
//   ],
// })

// PC端自动生成视频时显示声音引导
// export const showVideoSoundGuideOnAutoVideoDesktopState = atom<boolean>({
//   key: 'showVideoSoundGuideOnAutoVideoDesktopState',
//   default: false,
//   effects: [
//     localStorageWriteJsonEffect('showVideoSoundGuideOnAutoVideoDesktop'),
//   ],
// })

// 聊天设置小红点状态 - 记录用户是否查看过视频声音设置
// export const hasViewedVideoSoundSettingState = atom<boolean>({
//   key: 'hasViewedVideoSoundSettingState',
//   default: false,
//   effects: [localStorageWriteJsonEffect('hasViewedVideoSoundSetting')],
// })

// 聊天字号设置 - 14px(Small) / 15px(Medium) / 16px(Large)
export const chatFontSizeState = atom<14 | 15 | 16>({
  key: 'chatFontSizeState',
  default: 14, // 默认小号
  effects: [localStorageWriteJsonEffect('chatFontSize')],
})

// 聊天设置小红点状态 - 记录用户是否查看过字号设置
// export const hasViewedFontSizeSettingState = atom<boolean>({
//   key: 'hasViewedFontSizeSettingState',
//   default: false,
//   effects: [localStorageWriteJsonEffect('hasViewedFontSizeSetting')],
// })

// 聊天气泡颜色设置 - null 表示使用角色主题色，非 null 表示使用自定义颜色
export const chatBubbleColorState = atom<string | null>({
  key: 'chatBubbleColorState',
  default: null, // 默认使用角色主题色
  effects: [localStorageWriteJsonEffect('chatBubbleColor')],
})

// 当前聊天角色主题色（用于桌面端 ChatBubbleStyleModal 传递）
export const chatCurrentPrimaryColorState = atom<string>({
  key: 'chatCurrentPrimaryColorState',
  default: DEFAULT_CHAT_BUBBLE_COLOR,
})

// export const isFirstEnterStory = atom<boolean>({
//   key: 'isFirstEnterStory',
//   default: true,
//   effects: [localStorageWriteJsonEffect('isFirstEnterStory')],
// })
// export const isFirstTimeChooseModel = atom<boolean>({
//   key: 'isFirstTimeChooseModel',
//   default: true,
//   effects: [localStorageWriteJsonEffect('isFirstTimeChooseModel')],
// })

// Create a video 长视频生成的独立 loading 状态
export const chatMediaCreateOneTimeVideoLoadingState = atom<boolean>({
  key: 'chatMediaCreateOneTimeVideoLoadingState',
  default: false,
})

// Create a video 长视频生成进度
export const chatMediaCreateOneTimeVideoLoadingProgressState = atom<string>({
  key: 'chatMediaCreateOneTimeVideoLoadingProgressState',
  default: '0',
})

// Generate Reel 模式状态
export const isGenerateDramaModeEnabledState = atom<boolean>({
  key: 'isGenerateDramaModeEnabledState',
  default: false,
})

// 关系等级抽屉打开状态
export const relationshipDrawerOpenState = atom<boolean>({
  key: 'relationshipDrawerOpenState',
  default: false,
})

// 小等级升级动画显示状态
export const showSmallUpgradeAnimationState = atom<boolean>({
  key: 'showSmallUpgradeAnimationState',
  default: false,
})

// 大等级升级动画显示状态
export const showBigUpgradeAnimationState = atom<boolean>({
  key: 'showBigUpgradeAnimationState',
  default: false,
})

// 领取奖励动画显示状态
export const showClaimRewardAnimationState = atom<boolean>({
  key: 'showClaimRewardAnimationState',
  default: false,
})

// 升级后的新等级
export const upgradedLevelState = atom<{
  newLevel: number
  newSubLevel: number
  bigLevel: number
}>({
  key: 'upgradedLevelState',
  default: {
    newLevel: 0,
    newSubLevel: 0,
    bigLevel: 0,
  },
})

// 亲密度引导遮罩显示状态
// export const relationshipGuideOpenState = atom<boolean>({
//   key: 'relationshipGuideOpenState',
//   default: true,
//   effects: [localStorageWriteJsonEffect('relationshipGuideOpen')],
// })

// 关系抽屉动画状态
export const relationshipDrawerHasAnimationState = atom<boolean>({
  key: 'relationshipDrawerHasAnimationState',
  default: false,
})

// 关系抽屉动画的目标等级
export const relationshipDrawerNewLevelState = atom<number>({
  key: 'relationshipDrawerNewLevelState',
  default: 0,
})

// 关系抽屉各等级项高度列表
export const relationshipDrawerHeightListState = atom<string>({
  key: 'relationshipDrawerHeightListState',
  default: '[]',
})

// 小等级升级横幅显示状态（持久化存储，只显示一次）
// export const firstLevelUpBannerEnabledState = atom<boolean>({
//   key: 'firstLevelUpBannerEnabledState',
//   default: true,
//   effects: [localStorageWriteJsonEffect('firstLevelUpBannerEnabled')],
// })

// 小等级升级横幅当前是否可见
export const firstLevelUpBannerVisibleState = atom<boolean>({
  key: 'firstLevelUpBannerVisibleState',
  default: false,
})

// 小等级升级横幅显示的新等级
export const levelUpBannerNewLevelState = atom<number>({
  key: 'levelUpBannerNewLevelState',
  default: 0,
})

// 数字翻转动画状态
export const isLevelUpgradingState = atom<boolean>({
  key: 'isLevelUpgradingState',
  default: false,
})

// 数字翻转动画的新等级
export const levelUpgradeNewLevelState = atom<number>({
  key: 'levelUpgradeNewLevelState',
  default: 0,
})

// 首次奖励引导提示是否已显示（用于控制 ConnectedTooltip，不影响红点显示）
export const calendarRewardGuideOpenState = atom<boolean>({
  key: 'calendarRewardGuideOpenState',
  default: false,
  effects: [localStorageWriteJsonEffect('calendarRewardGuideOpen')],
})

// 等级奖励引导显示状态
export const showLevelRewardGuideState = atom<boolean>({
  key: 'showLevelRewardGuideState',
  default: false,
})

// 亲密度开关状态(每个角色独立)
export const relationshipEnabledMapState = atom<Map<string, boolean>>({
  key: 'relationshipEnabledMapState',
  default: new Map(),
})

export const hasShownValentineGuideState = atom<boolean>({
  key: 'hasShownValentineGuideState',
  default: false,
  effects: [localStorageWriteJsonEffect('hasShownValentineGuide')],
})

export const hasShownEasterGoldenEggGuideState = atom<boolean>({
  key: 'hasShownEasterGoldenEggGuideState',
  default: false,
  effects: [localStorageWriteJsonEffect('hasShownEasterGoldenEggGuide')],
})

export const hasShownEasterLuckyEggGuideState = atom<boolean>({
  key: 'hasShownEasterLuckyEggGuideState',
  default: false,
  effects: [localStorageWriteJsonEffect('hasShownEasterLuckyEggGuide')],
})
